import React, { useState } from 'react';
import {
  TextField,
  Button,
  Container,
  Box,
  Typography,
  InputAdornment,
  Alert,
  Collapse,
  ToggleButtonGroup,
  ToggleButton,
} from '@mui/material';
import {
  EmailRounded,
  LockResetRounded,
  CheckCircleRounded,
  AdminPanelSettingsRounded,
  SchoolRounded,
} from '@mui/icons-material';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import axios from 'axios';
import { AWS_LINK, DEV_LINK } from "../Static/static";

const validationSchema = Yup.object({
  email: Yup.string()
    .email('Invalid email address')
    .required('Email is required'),
  type: Yup.string()
    .oneOf(['admin', 'tutor'])
    .required('Please select an account type'),
});

const ACCOUNT_TYPES = [
  { value: 'tutor', label: 'Tutor',         icon: SchoolRounded },
  { value: 'admin', label: 'Administrator',  icon: AdminPanelSettingsRounded },
];

const ForgotPassword = () => {
  const [submitted, setSubmitted] = useState(false);
  const [errorMsg, setErrorMsg]   = useState('');
  const LINK = process.env.NODE_ENV === 'production' ? AWS_LINK : DEV_LINK;

  const formik = useFormik({
    initialValues: { email: '', type: 'tutor' },
    validationSchema,
    onSubmit: async (values, { setSubmitting }) => {
      setErrorMsg('');
      try {
        const response = await axios.post(`${LINK}/forgot_password`, {
          email: values.email,
          type:  values.type,
        });
        if (response.data.status === 'OK') {
          setSubmitted(true);
        }
      } catch (error) {
        setErrorMsg(
          error?.response?.data?.message ||
          'Something went wrong. Please try again.'
        );
      } finally {
        setSubmitting(false);
      }
    },
  });

  const selectedType = ACCOUNT_TYPES.find((t) => t.value === formik.values.type);

  return (
    <Container maxWidth="sm">
      <Box
        sx={{
          width: '100%',
          maxWidth: 420,
          margin: 'auto',
          mt: 10,
          p: 4,
          boxShadow: '0px 4px 24px rgba(0,0,0,0.08)',
          borderRadius: 1,
          position: 'relative',
          bgcolor: 'background.paper',
          '&:before': {
            content: '""',
            position: 'absolute',
            top: 0, left: 0, right: 0,
            height: 3,
            bgcolor: 'primary.main',
            borderTopLeftRadius: 4,
            borderTopRightRadius: 4,
          },
        }}
      >
        {/* ── Icon ── */}
        <Box sx={{ display: 'flex', justifyContent: 'center', mb: 2 }}>
          <Box
            sx={{
              width: 48,
              height: 48,
              borderRadius: 1,
              bgcolor: 'primary.main',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <LockResetRounded sx={{ color: 'white', fontSize: 24 }} />
          </Box>
        </Box>

        {/* ── Success state ── */}
        {submitted ? (
          <Box sx={{ textAlign: 'center' }}>
            <CheckCircleRounded sx={{ fontSize: 40, color: 'success.main', mb: 1.5 }} />
            <Typography variant="h6" fontWeight={700} gutterBottom>
              Check your inbox
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
              If a <strong>{selectedType?.label}</strong> account exists for{' '}
              <strong>{formik.values.email}</strong>, you'll receive a reset
              link shortly.
            </Typography>
            <Button
              variant="outlined"
              size="small"
              onClick={() => { setSubmitted(false); formik.resetForm(); }}
              sx={{ borderRadius: 1, textTransform: 'none' }}
            >
              Use a different email
            </Button>
          </Box>

        ) : (
          <>
            <Typography variant="h6" fontWeight={700} gutterBottom>
              Forgot password
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
              Select your account type and enter your email — we'll send you
              a link to reset your password.
            </Typography>

            {/* ── Error alert ── */}
            <Collapse in={!!errorMsg}>
              <Alert
                severity="error"
                variant="outlined"
                onClose={() => setErrorMsg('')}
                sx={{ borderRadius: 1, mb: 2, py: 0.5, fontSize: '0.82rem' }}
              >
                {errorMsg}
              </Alert>
            </Collapse>

            <Box component="form" onSubmit={formik.handleSubmit}>

              {/* ── Account type ── */}
              <Typography
                variant="overline"
                sx={{ fontSize: '0.65rem', letterSpacing: '0.1em', color: 'text.disabled', fontWeight: 700, display: 'block', mb: 0.75 }}
              >
                I am a…
              </Typography>
              <ToggleButtonGroup
                exclusive
                fullWidth
                value={formik.values.type}
                onChange={(_, val) => { if (val) formik.setFieldValue('type', val); }}
                sx={{ mb: 2.5 }}
              >
                {ACCOUNT_TYPES.map(({ value, label, icon: Icon }) => (
                  <ToggleButton
                    key={value}
                    value={value}
                    sx={{
                      borderRadius: 1,
                      textTransform: 'none',
                      fontWeight: 600,
                      fontSize: '0.82rem',
                      py: 1,
                      gap: 0.75,
                      '&.Mui-selected': {
                        bgcolor: 'primary.main',
                        color: 'white',
                        '&:hover': { bgcolor: 'primary.dark' },
                      },
                    }}
                  >
                    <Icon sx={{ fontSize: 18 }} />
                    {label}
                  </ToggleButton>
                ))}
              </ToggleButtonGroup>

              {/* ── Email ── */}
              <Typography
                variant="overline"
                sx={{ fontSize: '0.65rem', letterSpacing: '0.1em', color: 'text.disabled', fontWeight: 700, display: 'block', mb: 0.75 }}
              >
                Email address
              </Typography>
              <TextField
                fullWidth
                size="small"
                id="email"
                name="email"
                placeholder={`Your ${selectedType?.label?.toLowerCase()} email`}
                type="email"
                autoComplete="email"
                value={formik.values.email}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                error={formik.touched.email && Boolean(formik.errors.email)}
                helperText={formik.touched.email && formik.errors.email}
                sx={{ mb: 2.5, '& .MuiOutlinedInput-root': { borderRadius: 1 } }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <EmailRounded sx={{ fontSize: 18, color: 'text.disabled' }} />
                    </InputAdornment>
                  ),
                }}
              />

              <Button
                type="submit"
                fullWidth
                variant="contained"
                disabled={formik.isSubmitting || !formik.isValid || !formik.dirty}
                sx={{ borderRadius: 1, textTransform: 'none', fontWeight: 700, py: 1 }}
              >
                {formik.isSubmitting ? 'Sending…' : 'Send reset link'}
              </Button>
            </Box>
          </>
        )}
      </Box>
    </Container>
  );
};

export default ForgotPassword;